import { useState } from 'react';

const EMPTY = {
  title: '',
  categoryId: '',
  editionNo: '',
  editionTotal: '',
  size: '',
  material: '',
  releaseYear: new Date().getFullYear(),
  author: 'Kợt Nhả Decor',
  status: 'CON_HANG',
  ownerName: '',
  description: '',
  isPublished: true,
};

const NEW_MATERIAL = '__new__';

export default function ArtworkForm({
  initial,
  onSave,
  onCancel,
  categories = [],
  materials = [],
  heading,
}) {
  const isEdit = Boolean(initial.id);
  const [form, setForm] = useState({ ...EMPTY, ...initial });
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  // Chất liệu: chọn từ các chất liệu đã dùng, hoặc gõ chất liệu mới
  const [customMaterial, setCustomMaterial] = useState(false);
  const materialOptions = form.material && !materials.includes(form.material) ? [...materials, form.material] : materials;
  const handleMaterialSelect = (e) => {
    if (e.target.value === NEW_MATERIAL) {
      setCustomMaterial(true);
      setForm({ ...form, material: '' });
    } else {
      setForm({ ...form, material: e.target.value });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (form.editionNo && form.editionTotal && Number(form.editionNo) > Number(form.editionTotal)) {
      setError('Bản thứ không được lớn hơn tổng số bản');
      return;
    }
    setSaving(true);

    const data = {
      title: form.title,
      // Khi sửa: 0 = bỏ danh mục (null nghĩa là giữ nguyên)
      categoryId: form.categoryId ? Number(form.categoryId) : isEdit ? 0 : null,
      editionNo: form.editionNo ? Number(form.editionNo) : null,
      editionTotal: form.editionTotal ? Number(form.editionTotal) : null,
      size: form.size,
      material: form.material,
      releaseYear: form.releaseYear ? Number(form.releaseYear) : null,
      author: form.author,
      status: form.status,
      ownerName: form.ownerName,
      description: form.description || '', // gửi chuỗi rỗng để xoá được mô tả khi sửa
      isPublished: form.isPublished,
      useCategoryImage: !isEdit, // thêm tranh: backend copy ảnh danh mục (nếu có) thành ảnh chính
    };

    try {
      await onSave(data);
    } catch (err) {
      setError(`Lưu thất bại: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <form className="card form" onSubmit={handleSubmit}>
      <h2>{heading || (isEdit ? `Sửa tranh #${initial.id}` : 'Thêm tranh mới')}</h2>
      {/* Mã chứng nhận do hệ thống tự sinh khi lưu, không nhập hay sửa được */}
      <p className="muted form-code">
        {isEdit ? (
          <>
            Mã chứng nhận: <strong>{initial.code}</strong>
          </>
        ) : (
          'Mã chứng nhận sẽ được tạo tự động khi lưu.'
        )}
      </p>

      {/* Không có phần ảnh: ảnh tranh lấy theo ảnh danh mục */}
      <div className="grid">
        <label className="wide">
          Tác phẩm <span className="required">*</span>
          <input required value={form.title} onChange={set('title')} />
        </label>
        <div className="field">
          Danh mục
          <select value={form.categoryId || ''} onChange={set('categoryId')} aria-label="Danh mục">
            <option value="">— Không có —</option>
            {categories.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          Phiên bản (bản thứ / tổng số)
          <div className="input-row">
            <input type="number" min={1} value={form.editionNo ?? ''} onChange={set('editionNo')} aria-label="Bản thứ" />
            <span>/</span>
            <input type="number" min={1} value={form.editionTotal ?? ''} onChange={set('editionTotal')} aria-label="Tổng số bản" />
          </div>
        </div>
        <label>
          Kích thước <span className="required">*</span>
          <input required value={form.size || ''} onChange={set('size')} />
        </label>
        <div className="field">
          Chất liệu <span className="required">*</span>
          {customMaterial ? (
            <div className="input-row">
              <input required autoFocus value={form.material} onChange={set('material')} aria-label="Chất liệu mới" />
              <button type="button" onClick={() => setCustomMaterial(false)}>
                Huỷ
              </button>
            </div>
          ) : (
            <select required value={form.material || ''} onChange={handleMaterialSelect} aria-label="Chất liệu">
              <option value="">— Chọn chất liệu —</option>
              {materialOptions.map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
              <option value={NEW_MATERIAL}>+ Chất liệu khác…</option>
            </select>
          )}
        </div>
        <label>
          Năm phát hành
          <input type="number" value={form.releaseYear || ''} onChange={set('releaseYear')} />
        </label>
        <label>
          Tác giả <span className="required">*</span>
          <input required value={form.author || ''} onChange={set('author')} />
        </label>
        <label>
          Chủ sở hữu hiện tại <span className="required">*</span>
          <input required value={form.ownerName || ''} onChange={set('ownerName')} />
        </label>
        <label className="wide">
          Mô tả tác phẩm
          <textarea
            rows={4}
            value={form.description || ''}
            onChange={set('description')}
          />
        </label>
      </div>

      {error && <p className="error">{error}</p>}

      <div className="form-actions">
        <button type="button" onClick={onCancel}>
          Đóng
        </button>
        <button type="submit" className="primary" disabled={saving}>
          {saving ? 'Đang lưu...' : 'Lưu'}
        </button>
      </div>
    </form>
  );
}
